import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  ScanBarcode,
  Package,
  Layers,
  Boxes,
  Receipt,
  BarChart3,
  Users as UsersIcon,
  LogOut,
  ChevronRight,
} from "lucide-react";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, admin: true },
  { to: "/pos", label: "Punto de Venta", icon: ScanBarcode, admin: false },
  { to: "/admin/products", label: "Productos", icon: Package, admin: true },
  { to: "/admin/categories", label: "Categorías", icon: Layers, admin: true },
  { to: "/admin/inventory", label: "Inventario", icon: Boxes, admin: true },
  { to: "/admin/sales", label: "Historial Ventas", icon: Receipt, admin: false },
  { to: "/admin/reports", label: "Reportes", icon: BarChart3, admin: true },
  { to: "/admin/users", label: "Usuarios", icon: UsersIcon, admin: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-bone">
      {/* Sidebar */}
      <aside
        className="w-64 shrink-0 bg-stone text-bone flex flex-col border-r border-stone/10"
        data-testid="app-sidebar"
      >
        <div className="px-6 py-7 border-b border-bone/10">
          <Link to={isAdmin ? "/admin" : "/pos"} className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-clay flex items-center justify-center font-outfit font-bold text-xl text-white shadow-[0_4px_14px_rgba(200,90,64,0.4)]">
              L
            </div>
            <div>
              <div className="font-outfit font-bold text-lg leading-none">Losería</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-bone/50 mt-1">
                Punto de venta
              </div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems
            .filter((it) => isAdmin || !it.admin)
            .map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/admin"}
                  data-testid={`nav-${it.to.replaceAll("/", "-")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-figtree transition-all ${
                      isActive
                        ? "bg-clay text-white shadow-[0_2px_10px_rgba(200,90,64,0.4)]"
                        : "text-bone/70 hover:bg-bone/5 hover:text-bone"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1">{it.label}</span>
                  <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                </NavLink>
              );
            })}
        </nav>

        <div className="px-4 py-4 border-t border-bone/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-bone/10 flex items-center justify-center text-bone font-outfit font-semibold">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="current-user-name">
                {user?.name}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-bone/50">
                {user?.role === "admin" ? "Administrador" : "Cajero"}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-bone/5 hover:bg-clay hover:text-white text-bone/80 text-sm font-figtree transition-all"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-y-auto">{children}</div>
      </main>
    </div>
  );
}
